import React from 'react';
import { Box, Image, Button, HStack, VStack, Text } from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';

/**
 * Normalizes document URLs from various formats (array, string, object) to an array of valid URLs
 */
export const normalizeDocumentUrls = (value) => {
  try {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .filter(Boolean)
        .map((item) => {
          const url = typeof item === 'string' ? item : (item?.url || null);
          if (!url || typeof url !== 'string') return null;
          const trimmed = url.trim();
          if (!trimmed) return null;
          if (/^https?:\/\//i.test(trimmed)) return trimmed;
          // Handle Supabase storage paths
          const supabaseBase = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
          if (supabaseBase && /storage\/v1\/object\/public\//.test(trimmed)) {
            return trimmed.startsWith('http') ? trimmed : `${supabaseBase}/${trimmed.replace(/^\//, '')}`;
          }
          return null;
        })
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        try {
          const arr = JSON.parse(trimmed);
          if (Array.isArray(arr)) {
            return arr
              .map((item) => {
                const url = typeof item === 'string' ? item : (item?.url || null);
                return url && /^https?:\/\//i.test(url) ? url : null;
              })
              .filter(Boolean);
          }
        } catch {}
      }
      if (/^https?:\/\//i.test(trimmed)) return [trimmed];
    }
    return [];
  } catch {
    return [];
  }
};

/**
 * Checks if a URL is an image file
 */
export const isImageFile = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(url) || /^data:image\//i.test(url);
};

/**
 * Checks if a URL is a PDF file
 */
export const isPdfFile = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.endsWith('.pdf') || /\.pdf(\?|$)/i.test(url);
};

/**
 * DocumentPreview component - displays document images/PDFs with consistent styling
 * Used across Admin, Phlebotomist, and Lab portals
 */
export default function DocumentPreview({ 
  urls, 
  title, 
  showTitle = true,
  maxHeight = 250,
  showViewButton = true,
  size = 'md' // 'sm', 'md', 'lg'
}) {
  const normalizedUrls = normalizeDocumentUrls(urls);
  
  if (normalizedUrls.length === 0) {
    return null;
  }

  const sizeMap = {
    sm: { imgHeight: 150, imgWidth: 200 },
    md: { imgHeight: 250, imgWidth: 300 },
    lg: { imgHeight: 400, imgWidth: 500 }
  };

  const { imgHeight, imgWidth } = sizeMap[size] || sizeMap.md;

  return (
    <Box>
      {showTitle && title && (
        <Text fontWeight="bold" mb={2} fontSize="sm" color="gray.700">
          {title}
        </Text>
      )}
      <VStack align="start" spacing={3}>
        {normalizedUrls.map((url, idx) => {
          const isImage = isImageFile(url);
          const isPdf = isPdfFile(url);
          const displayTitle = normalizedUrls.length > 1 ? `${title || 'Document'} ${idx + 1}` : (title || 'Document');

          return (
            <Box key={idx} w="full">
              <Box
                mb={2}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                bg="gray.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                minH={`${imgHeight}px`}
                maxH={`${imgHeight}px`}
                maxW={`${imgWidth}px`}
              >
                {isImage ? (
                  <Box position="relative" w="full" h="full">
                    <Image
                      src={url}
                      alt={displayTitle}
                      maxH={`${imgHeight}px`}
                      maxW={`${imgWidth}px`}
                      objectFit="contain"
                      fallback={
                        <Box p={4} textAlign="center" w="full">
                          <Text fontSize="sm" color="gray.500" mb={2}>Image not available</Text>
                          <Button as="a" href={url} target="_blank" size="sm" colorScheme="blue">
                            View File
                          </Button>
                        </Box>
                      }
                    />
                  </Box>
                ) : isPdf ? (
                  <Box p={4} textAlign="center">
                    <Text fontSize="4xl" mb={2}>📄</Text>
                    <Text fontSize="sm" color="gray.600">PDF Document</Text>
                  </Box>
                ) : (
                  <Box p={4} textAlign="center">
                    <Text fontSize="sm" color="gray.500">Document</Text>
                  </Box>
                )}
              </Box>
              {showViewButton && (
                <Button
                  as="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  colorScheme="blue"
                  size="sm"
                  leftIcon={<AttachmentIcon />}
                >
                  View {displayTitle}
                </Button>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

